"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { searchRepository } from "../../data/repositories/search.repository";
import type { SearchResultsResponse, SearchType } from "../../types";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const PAGE_LIMIT = 12;

interface UseSearchOptions {
  initialQuery?: string;
  initialType?: SearchType;
  initialData?: SearchResultsResponse | null;
}

export interface UseSearchResult {
  query: string;
  setQuery: (value: string) => void;
  type: SearchType;
  setType: (value: SearchType) => void;
  data: SearchResultsResponse | null;
  loading: boolean;
  error: boolean;
  hasSearched: boolean;
  clear: () => void;
}

/**
 * Debounced live search backed by the public `/search` endpoint.
 * Cancels in-flight requests when the query changes.
 */
export function useSearch({
  initialQuery = "",
  initialType = "all",
  initialData = null,
}: UseSearchOptions = {}): UseSearchResult {
  const [query, setQuery] = useState(initialQuery);
  const [type, setType] = useState<SearchType>(initialType);
  const [data, setData] = useState<SearchResultsResponse | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(Boolean(initialData));

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (rawQuery: string, searchType: SearchType) => {
    const trimmed = rawQuery.trim();

    abortRef.current?.abort();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setData(null);
      setLoading(false);
      setError(false);
      setHasSearched(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(false);

    try {
      const response = await searchRepository.getResults(
        { q: trimmed, type: searchType, limit: PAGE_LIMIT, includeFacets: false },
        controller.signal,
      );
      setData(response);
      setHasSearched(true);
    } catch {
      if (controller.signal.aborted) {
        return;
      }
      setError(true);
      setData(null);
      setHasSearched(true);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      void runSearch(query, type);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, type, runSearch]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const clear = useCallback(() => {
    setQuery("");
    setData(null);
    setError(false);
    setHasSearched(false);
  }, []);

  return {
    query,
    setQuery,
    type,
    setType,
    data,
    loading,
    error,
    hasSearched,
    clear,
  };
}
